import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { ModuleType, PageType, SubPageType } from 'src/app/shared/enums/page-type.enum';
import { ModalService } from 'src/app/shared/services/modal.service';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { StorageService } from 'src/app/shared/services/storage.service';
import { ContactUsModalComponent } from '../components/_modals/contact-us-modal/contact-us-modal.component';
import { MathHelper } from '../helpers/math.helper';
import { DataAvailabilityInfo } from '../types/data-availability-info.type';
import { Data } from '../types/data.type';
import { BadgeType, MenuSection, MenuSettings } from '../types/menu-section.type';
import { AuthService } from './auth.service';
import { NotificationsService } from './notifications.service';
import { MonitoringStatisticsService } from './monitoring-statistics.service';
import { PermissionHelper } from '../helpers/permission.helper';
import { AppConfigurationService } from './app-configuration.service';
import { ClientPermission } from '../enums/generated.enums';
import { CalendarEventService } from '../../modules/orchestration/calendar/calendar-event.service';
import { TenantsService } from './tenants.service';

@Injectable({ providedIn: 'root' })
export class MenuService {
  private menuSettingsSubject = new BehaviorSubject<MenuSettings>({ sections: [], showTenants: false });

  get menuSections(): MenuSection[] {
    return this.menuSettingsSubject.getValue().sections;
  }

  get menuSettings$(): Observable<MenuSettings> {
    return this.menuSettingsSubject.asObservable();
  }

  constructor(
    private navigationService: NavigationService,
    private modalService: ModalService,
    private storageService: StorageService,
    private authService: AuthService,
    private notificationService: NotificationsService,
    private statisticsService: MonitoringStatisticsService,
    private calendarEventService: CalendarEventService,
    private appConfigurationService: AppConfigurationService,
    private data: Data,
    private tenantsService: TenantsService,
  ) {}

  clearMenuItems(): void {
    this.menuSettingsSubject.next({ sections: [], showTenants: false });
  }

  public initializeMainMenuItems(dataAvailability: DataAvailabilityInfo): void {
    let menuSections: MenuSection[] = [
      {
        label: 'Monitoring',
        type: 'items',
        items: [
          {
            route: this.navigationService.getMonitoringRoute(PageType.Processes),
            label: 'Processes',
            icon: 'processes-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Monitoring, PageType.Processes),
            pageType: PageType.Processes,
            badge$: this.statisticsService.statistics$.pipe(map(s => s?.quarantinedProcessCount)),
            badgeType: BadgeType.Warning,
            hideBadgeWhenOnPage: true,
          },
          {
            route: this.navigationService.getMonitoringRoute(PageType.Resources),
            activeRoutes: [
              this.navigationService.getMonitoringRoute(PageType.Resources),
              this.navigationService.getMonitoringRoute(PageType.ResourceGroups),
              this.navigationService.getMonitoringRoute(PageType.Credentials),
              this.navigationService.getMonitoringRoute(PageType.CredentialPools),
            ],
            label: 'Machines ',
            icon: 'resources-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Monitoring, PageType.Resources),
            pageType: PageType.Resources,
            badge$: this.statisticsService.statistics$.pipe(
              map(s => (s?.quarantinedResourceCount ?? 0) + (s?.quarantinedLoginUserCount ?? 0))
            ),
            badgeType: BadgeType.Warning,
            hideBadgeWhenOnPage: true,
          },
          {
            route: this.navigationService.getMonitoringRoute(PageType.Sessions),
            label: 'Process runs',
            icon: 'sessions-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Monitoring, PageType.Sessions),
            pageType: PageType.Sessions,
          },
          {
            route: this.navigationService.getUtilizationRoute(this.storageService.activeUtilizationSubPage),
            label: 'Utilization',
            icon: 'utilization-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Monitoring, PageType.Utilization),
            pageType: PageType.Utilization,
            isHidden: !this.tenantsService.showUtilization,
            onSubPageChange: s => (this.storageService.activeUtilizationSubPage = s as SubPageType.Licenses | SubPageType.Resources),
            tabs: [
              {
                route: this.navigationService.getUtilizationRoute(SubPageType.Licenses),
                label: 'Licenses',
                subPageType: SubPageType.Licenses,
                isHidden: !this.tenantsService.showUtilization,
              },
              {
                route: this.navigationService.getUtilizationRoute(SubPageType.Resources),
                label: 'Machines',
                subPageType: SubPageType.Resources,
                isHidden: !this.tenantsService.showUtilization,
              },
            ],
          },
          {
            route: this.navigationService.getMonitoringRoute(PageType.PastSchedule),
            label: 'Past schedule',
            icon: 'schedule-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Monitoring, PageType.PastSchedule),
            pageType: PageType.PastSchedule,
          },
        ],
      },
      {
        label: 'Orchestration',
        type: 'items',
        items: [
          {
            route: this.navigationService.getAlertsRoute(),
            label: 'Alerts',
            icon: 'alert-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Orchestration, PageType.Alerts),
            pageType: this.navigationService.alertsPageType,
            badge$: this.notificationService.unreadAlertsCount$,
            badgeType: BadgeType.Warning,
            badgeText: this.notificationService.isNotificationsV2 ? '!' : undefined, // v2 counts active problems, not new ones
            hideBadgeWhenOnPage: true,
          },
          {
            route: this.navigationService.getCalendarRoute(),
            label: 'Calendar',
            icon: 'calendar-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Orchestration, PageType.Calendar),
            pageType: PageType.Calendar,
            badge$: this.calendarEventService.ongoingMaintenanceWindowsCount$,
            badgeType: BadgeType.Warning,
          },
          {
            route: this.navigationService.getExternalSystemsRoute(),
            label: 'External systems',
            icon: 'external-systems-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Orchestration, PageType.ExternalSystems),
            pageType: PageType.ExternalSystems,
            badge$: this.statisticsService.statistics$.pipe(map(s => s?.quarantinedExternalSystemCount)),
            badgeType: BadgeType.Warning,
            hideBadgeWhenOnPage: true,
          },
          {
            // FUTURE page - should be hidden
            label: 'Process configuration',
            icon: 'process-config-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Orchestration, PageType.ProcessConfiguration),
            pageType: PageType.ProcessConfiguration,
            disabled: true,
            isHidden: true,
          },
          {
            route: this.navigationService.getDumpViewerRoute(),
            label: 'Dump viewer',
            icon: 'view',
            permission: PermissionHelper.getPagePermission(ModuleType.Orchestration, PageType.DumpViewer, SubPageType.DumpViewer),
            featureFlag: PermissionHelper.getPageFeatureFlag(ModuleType.Orchestration, PageType.DumpViewer, SubPageType.DumpViewer),
            pageType: PageType.DumpViewer,
            tabs: [
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.DumpViewer),
                label: 'Dump viewer',
                subPageType: SubPageType.DumpViewer,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.Processes),
                label: 'Processes',
                subPageType: SubPageType.Processes,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.Sessions),
                label: 'Process runs',
                subPageType: SubPageType.Sessions,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.EventLog),
                label: 'Event log',
                subPageType: SubPageType.EventLog,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.PastSchedule),
                label: 'Past schedule',
                subPageType: SubPageType.PastSchedule,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.Queues),
                label: 'Queues',
                subPageType: SubPageType.Queues,
              },
              {
                route: this.navigationService.getDumpViewerRoute(SubPageType.Calendar),
                label: 'Calendar',
                subPageType: SubPageType.Calendar,
              },
            ],
          },
        ],
      },
      {
        label: 'Analytics',
        type: 'items',
        items: [
          {
            route: this.navigationService.getDetectedIncidentsRoute(),
            label: 'Detected incidents',
            icon: 'incidents-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Analytics, PageType.DetectedIncidents),
            pageType: PageType.DetectedIncidents,
          },
          {
            route: this.navigationService.getAnomaliesRoute(),
            label: 'Anomalies',
            icon: 'anomalies-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Analytics, PageType.Anomalies),
            pageType: PageType.Anomalies,
            badge$: this.notificationService.unreadAnomaliesCount$,
            badgeType: BadgeType.Warning,
          },
          {
            route: this.navigationService.getDiagnosticDashboardRoute(),
            label: 'Diagnostic dashboard',
            icon: 'statistics-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Analytics, PageType.DiagnosticDashboard),
            pageType: PageType.DiagnosticDashboard,
          },
          {
            route: this.navigationService.getRuntimeAnalysesRoute(),
            label: 'Runtime analysis',
            icon: 'runtime-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Analytics, PageType.RuntimeAnalysis),
            pageType: PageType.RuntimeAnalysis,
          },
        ],
      },
      {
        label: 'Reporting',
        type: 'items',
        items: [
          {
            route: this.navigationService.getExecutiveOverviewRoute(),
            label: 'Executive overview',
            icon: 'executive-overview-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Reporting, PageType.ExecutiveOverview),
            pageType: PageType.ExecutiveOverview,
          },
          {
            route: this.navigationService.getProcessesReportingRoute(SubPageType.ProcessReportList),
            label: 'Process reporting',
            icon: 'process-reporting-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Reporting, PageType.ProcessReporting),
            pageType: PageType.ProcessReporting,
            activeRoutes: [
              this.navigationService.getProcessesReportingRoute(SubPageType.ProcessReportList),
              this.navigationService.getProcessesReportingRoute(SubPageType.ProcessReportDetail),
            ],
          },
          {
            route: this.navigationService.getMonthlyReportsRoute(),
            label: 'Monthly reports',
            icon: 'reports-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Reporting, PageType.MonthlyReports),
            pageType: PageType.MonthlyReports,
          },
          {
            route: this.navigationService.getBenchmarkOverviewRoute(),
            label: 'Benchmarking',
            icon: 'benchmarking-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Reporting, PageType.BenchmarkOverview),
            pageType: PageType.BenchmarkOverview,
          },
        ],
      },
      {
        label: '',
        type: 'actions',
        items: [
          {
            route: this.navigationService.getOrganizationSettingsRoute(),
            label: 'Organization settings',
            icon: 'configuration-24',
            permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings),
            pageType: PageType.OrganizationSettings,
            tabs: [
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.GeneralSettings),
                label: 'General',
                subPageType: SubPageType.GeneralSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.GeneralSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.TenantsSettings),
                label: 'Tenants',
                subPageType: SubPageType.TenantsSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.TenantsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ProcessesSettings),
                label: 'Processes',
                subPageType: SubPageType.ProcessesSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ProcessesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ResourcesSettings),
                label: 'Machines',
                subPageType: SubPageType.ResourcesSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ResourcesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.QueuesSettings),
                label: 'Queues',
                subPageType: SubPageType.QueuesSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.QueuesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.UsersSettings),
                label: 'Users',
                subPageType: SubPageType.UsersSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.UsersSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.NotificationsSettings),
                label: 'Email notifications',
                subPageType: SubPageType.NotificationsSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.NotificationsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ApiCredentialsSettings),
                label: 'API credentials',
                subPageType: SubPageType.ApiCredentialsSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ApiCredentialsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.MsTeamsSettings),
                label: 'Microsoft Teams',
                subPageType: SubPageType.MsTeamsSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.MsTeamsSettings),
                featureFlag: PermissionHelper.getPageFeatureFlag(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.MsTeamsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.AnomaliesSettings),
                label: 'Anomalies',
                subPageType: SubPageType.AnomaliesSettings,
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.AnomaliesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.RecoveryRulesSettings),
                label: 'Recovery rules',
                subPageType: SubPageType.RecoveryRulesSettings,
                group: 'Orchestration',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.RecoveryRulesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ExternalServicesSettings),
                label: 'Integrations',
                subPageType: SubPageType.ExternalServicesSettings,
                group: 'Orchestration',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ExternalServicesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ConditionStates),
                label: 'Condition states',
                subPageType: SubPageType.ConditionStates,
                group: 'Orchestration',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ConditionStates),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.PendingSessions),
                label: 'Pendings',
                subPageType: SubPageType.PendingSessions,
                group: 'Orchestration',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.PendingSessions),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.OrchestrationDebugging),
                label: 'Debugging',
                subPageType: SubPageType.OrchestrationDebugging,
                group: 'Orchestration',
                isHidden: !this.appConfigurationService.isOnPremise && !this.authService.hasPermission(ClientPermission.SystemAdmin),
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.OrchestrationDebugging),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ReportingSettings),
                label: 'Metrics',
                subPageType: SubPageType.ReportingSettings,
                group: 'Reporting',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ReportingSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.PdfReportsSettings),
                label: 'PDF reports',
                subPageType: SubPageType.PdfReportsSettings,
                group: 'Reporting',
                isHidden: !this.authService.hasPermission(ClientPermission.Reporting),
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.PdfReportsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.BusinessProcessesSettings),
                label: 'Business processes',
                subPageType: SubPageType.BusinessProcessesSettings,
                group: 'Reporting',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.BusinessProcessesSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.SavingsSettings),
                label: 'Costs and savings',
                subPageType: SubPageType.SavingsSettings,
                group: 'Reporting',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.SavingsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.GroupsSettings),
                label: 'Process groups',
                subPageType: SubPageType.GroupsSettings,
                group: 'Reporting',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.GroupsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.BillingGroupsSettings),
                label: 'Billing groups',
                subPageType: SubPageType.BillingGroupsSettings,
                group: 'Reporting',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.BillingGroupsSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.BenchmarkingSettings),
                label: 'Benchmarking',
                subPageType: SubPageType.BenchmarkingSettings,
                group: 'Reporting',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.BenchmarkingSettings),
              },
              {
                route: this.navigationService.getOrganizationSettingsRoute(SubPageType.ImportExportSettings),
                label: 'Import/Export',
                subPageType: SubPageType.ImportExportSettings,
                group: 'Reporting',
                permission: PermissionHelper.getPagePermission(ModuleType.Settings, PageType.OrganizationSettings, SubPageType.ImportExportSettings),
              },
            ],
          },
          {
            route: this.navigationService.getAuditLogRoute(),
            label: 'Audit log',
            icon: 'audit-log',
            permission: PermissionHelper.getPagePermission(ModuleType.Orchestration, PageType.AuditLog),
            disabled: !dataAvailability.isValid,
            isHidden: !dataAvailability.isValid,
            pageType: PageType.AuditLog,
          },
          {
            label: 'Help & feedback',
            icon: 'help-24',
            dataTestId: 'help-feedback',
            onClick: () => this.modalService.open(ContactUsModalComponent),
          },
        ],
      },
    ];

    menuSections.forEach(m => (m.items = m.items.filter(i => i.permission == null || this.authService.hasPermission(i.permission))));
    menuSections.forEach(m => (m.items = m.items.filter(i => i.featureFlag == null || this.authService.hasFeatureFlag(i.featureFlag))));
    menuSections.forEach(m => (m.items = m.items.filter(i => i.isHidden !== true)));
    menuSections = menuSections.filter(m => m.items.length > 0);

    if (!dataAvailability.isValid) {
      menuSections = menuSections.filter(m => m.type == 'actions');
    }

    const itemCount = MathHelper.sum(
      menuSections.filter(section => section.type === 'items' || section.type === 'submenu'),
      s => s.items.length,
    );

    this.menuSettingsSubject.next({
      sections: menuSections,
      isMenuExpanded: itemCount < 3 ? false : null,
      showTenants: true,
    });
  }

  public initializeAdminMenuItems(): void {
    const menuSections: MenuSection[] = [
      {
        label: 'Admin',
        type: 'submenu',
        parentRoute: ['/'],
        items: [
          {
            route: this.navigationService.getAdminOrganizationsRoute(),
            label: 'Organizations',
            icon: 'organization-24',
          },
          {
            route: this.navigationService.getAdminUsersRoute(),
            label: 'Users',
            icon: 'account-24',
          },
          {
            route: this.navigationService.getAdminLicensesRoute(),
            label: 'Licenses',
            icon: 'shield-check-24',
          },
          {
            route: this.navigationService.getAdminToolingRoute(),
            label: 'Tooling',
            icon: 'tools-24',
          },
        ],
      },
      {
        label: '',
        type: 'actions',
        items: [
          {
            label: 'Help & feedback',
            icon: 'help-24',
            onClick: () => this.modalService.open(ContactUsModalComponent),
          },
        ],
      },
    ];

    this.menuSettingsSubject.next({ sections: menuSections, showTenants: false });
  }

  public initializeRuntimeAnalysisMenuItems(processId: string): void {
    const menuSections: MenuSection[] = [
      {
        label: 'Runtime analysis',
        type: 'submenu',
        parentRoute: this.navigationService.getRuntimeAnalysesRoute(),
        items: [
          {
            route: this.navigationService.getOverviewRoute(processId),
            label: 'Overview',
            icon: 'overview-24',
          },
          {
            route: this.navigationService.getActionableInsightsRoute(processId),
            label: 'Actionable insights',
            icon: 'insight-24',
            badge$: this.data.actionableInsightsByStateChanged.pipe(map(() => this.data.masterProcess?.actionableInsightsByState.get('')?.size)),
            badgeType: BadgeType.Count,
          },
          {
            route: this.navigationService.getProcessMapRoute(processId),
            label: 'Process map',
            icon: 'map-24',
          },
          {
            route: this.navigationService.getVariantAnalysisRoute(processId),
            label: 'Variant analysis',
            icon: 'variant-analysis-24',
          },
          {
            route: this.navigationService.getCostAnalysisRoute(processId),
            label: 'Cost analysis',
            icon: 'cost-analysis-24',
          },
        ],
      },
      {
        label: '',
        type: 'actions',
        items: [
          {
            route: [],
            label: 'Assign this analysis',
            icon: 'assign',
            disabled: true,
            isHidden: true,
          },
          {
            route: [],
            label: 'Analysis settings',
            icon: 'settings',
            disabled: true,
            isHidden: true,
          },
          {
            label: 'Help & feedback',
            icon: 'help-24',
            onClick: () => this.modalService.open(ContactUsModalComponent),
          },
        ],
      },
    ];

    this.menuSettingsSubject.next({ sections: menuSections, showTenants: false });
  }
}
